// Northeastern University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Northeastern's Common Data Set 2025-2026, published as a web page by
//    University Decision Support (uds.northeastern.edu; the page opens on
//    2025-2026, and earlier years are PDFs): C1 applicants/admits/enrollees
//    for Fall 2025 with the residency table; C8 test policy for Fall 2027;
//    C21/C22 early plans. Quirk: the C1 table by sex adds to 105,257
//    applicants; the residency table's total row says 105,256, which is the
//    figure used here. Quirk: CDS G1 lists food and housing as $22,882 while
//    its own housing-only ($13,612) and food-only ($9,210) rows add to
//    $22,822; the cost table below uses the admissions cost page, whose items
//    add exactly to its $98,322 total.
//  - admissions.northeastern.edu (Deadlines and Decisions, Required
//    Materials, First-Year Applicants (checked, agrees on testing), Cost and
//    Financial Aid).
//  - College Scorecard, school id 167358 (net price by family income). The
//    API's DEMO_KEY was rate-limited on 2026-10-01, so the figures were read
//    from the Scorecard's own school page, which shows the same latest data.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Northeastern Common Data Set 2025-2026',
        url: 'https://uds.northeastern.edu/university-facts/common-data-set/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Northeastern Admissions, Deadlines and Decisions',
        url: 'https://admissions.northeastern.edu/academics/specialized-entry-programs/admissions-deadlines-decisions/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Northeastern Admissions, Required Materials',
        url: 'https://admissions.northeastern.edu/application-information/required-materials/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Northeastern Admissions, Cost and Financial Aid',
        url: 'https://admissions.northeastern.edu/cost-financial-aid/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?167358-Northeastern-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'northeastern-university',
    kbId: 'northeastern_university',
    name: 'Northeastern University',
    shortName: 'Northeastern',
    aliases: ['NEU'],
    location: 'Boston, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Northeastern brand colors); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#C8102E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/northeastern_university_logo.png',
    },

    // The direct answer and short FAQ (#4229): every figure restates one the
    // page already shows, from the source named beside it.
    // The search it answers: northeastern acceptance rate out of state.
    faq: {
        answer: { text: 'Northeastern University admitted 5.2% of out-of-state first-year applicants for fall 2025, 3,801 of the 72,870 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1, residency table' },
        questions: [
            { q: 'What is the Northeastern acceptance rate?', a: 'For fall 2025 entry, Northeastern admitted 5,920 of 105,256 first-year applicants, an acceptance rate of 5.6%, and 2,736 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Northeastern acceptance rate for in-state students?', a: 'For fall 2025, Northeastern admitted 1,384 of 13,365 in-state (Massachusetts) first-year applicants, 10.4%, against 5.2% for out-of-state applicants.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the Northeastern early decision acceptance rate?', a: 'For fall 2025, Northeastern admitted 1,910 of 5,404 Early Decision I and II applicants, 35.3%. The Common Data Set gives no early action counts.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'Is Northeastern test optional for 2027?', a: 'Yes. Northeastern is test-optional for the foreseeable future and does not require the SAT or ACT, and you are not penalized for applying without scores. If you send scores, they are considered, and Northeastern uses your best result between the SAT and the ACT.', source: 'requirements' },
            { q: 'What are Northeastern\'s deadlines for fall 2027?', a: 'Early Decision I and Early Action are due November 1, with decisions by January 1 and by February 15. Early Decision II and Regular Decision are due January 1, with decisions by March 1 and by April 1.', source: 'deadlines' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 105256,
        admitted: 5920,
        enrolled: 2736,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 13365, admitted: 1384 },
            { group: 'Out-of-state', applied: 72870, admitted: 3801 },
            { group: 'International', applied: 18754, admitted: 735 },
            { group: 'Unknown', applied: 267, admitted: 0 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Northeastern\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college, campus or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025. It gives no early action counts.',
        rows: [
            { plan: 'Early Decision I and II', applied: 5404, admitted: 1910 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Northeastern through a campus and program of your choice. Some majors ask for more than the Common App:',
        rows: [
            { school: 'Performing Arts, New York City', requirement: 'Applicants are required to audition or submit additional materials. Audition dates appear in your Application Status Check after you apply.' },
            { school: 'College of Arts, Media and Design', requirement: 'A portfolio is encouraged, not required. You submit it in your Application Status Check after you apply.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By January 1', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'By February 15', binding: false },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'By March 1', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'By April 1', binding: false },
        ],
        note: 'The FAFSA and CSS Profile are due November 15 for Early Decision I, December 1 for Early Action, January 15 for Early Decision II and February 15 for Regular Decision.',
        source: 'deadlines',
        binding: 'Early decision is binding when you are admitted to your first-choice program, or after choosing to be open to any Northeastern program: you are committed to attending and must withdraw your other applications. If you are admitted to a program that was not your first choice, the decision is not binding. Early action and regular decision are not binding.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Northeastern is test-optional for the foreseeable future and does not require the SAT or ACT. If you apply without scores, you are not penalized or disadvantaged. It is not test-blind: if you send scores, they are considered, and Northeastern uses your best result between the SAT and the ACT.',
        source: 'requirements',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71050 },
            { item: 'Fees', amount: 1650 },
            { item: 'Housing', amount: 13612 },
            { item: 'Food', amount: 9210 },
            { item: 'Books and course materials', amount: 1000 },
            { item: 'Personal expenses', amount: 900 },
            { item: 'Transportation', amount: 900 },
        ],
        total: 98322,
        note: 'For first-year students on the Boston, New York City or Oakland campuses. Housing and food vary with the options you choose. The London Scholars, New York City Scholars and N.U.in programs are priced separately.',
        source: 'costs',
        netPrice: {
            average: 29060,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: 2038 },
                { income: '$75,001 to $110,000', amount: 11758 },
                { income: 'Over $110,000', amount: 49705 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 and $30,001 to $48,000 bands. Your own price comes from Northeastern\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
